import { Button, Dialog, FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { Lock, LockOpen } from '@material-ui/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import useDialogAriaHidden from '../../../../hooks/useDialogAriaHidden';
import en from '../../../../lang/en-us';
import { SubmitButton } from '../../../../lib/Button';
import { TableColumn, TableWithCollapse } from '../../../../lib/DataTable';
import { getPortfolio } from '../../../../redux-store/actions/portfolio';
import { RootStateType } from '../../../../redux-store/reducers';
import { RegisterWith } from '../../../../redux-store/types/login';
import {
    LIST_PROTFOLIO_PAYLOAD,
    PORTFOLIO_DATA_RESPONSE,
    SCHEMES_RESPONSE,
} from '../../../../redux-store/types/portfolio';
import { FolioLockInfo } from './types';
import {
    EQUITY,
    LIQUID,
    DEBT,
    OTHER,
    LOCK_UNLOCK_FOLIOS,
    isMobileDevice,
} from '../../../../utils/constants';
import NoFoliosFound from '../NoFoliosFound';
import './lock-unlock-folios.scss';
import { useTableWithCollapse } from '../../../../lib/DataTable/customHooks';
import { ActionHeader, FaqLink } from '..';
import {
    getMatchAndUnMatchedFunds,
    maskEmail,
    maskMobileNumber,
} from '../../../../utils/utilityFunctions';
import {
    fundsByLienFlag,
    getErrorMessage,
    portfoliosByAvailableUnits,
    portfoliosByMinorStatus,
    portfoliosByTransactionResource,
    decimalFix,
} from '../../portfolios/utils';

const lockUnlockFolioFilters: Function[] = [
    portfoliosByTransactionResource,
    portfoliosByMinorStatus,
    portfoliosByAvailableUnits,
    fundsByLienFlag,
    // modeOfHoldingSingle,
];

const stripDialCode = (mobile: string = '') => {
    const digits = (mobile || '').replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
};

const { dashboard, dbCommercial, common } = en;

const _renderStatus = ({ row }: { row: FolioLockInfo }) => {
    const isLocked = row.status === 'Locked';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isLocked ? (
                <Lock style={{ color: '#f44336' }} />
            ) : (
                <LockOpen style={{ color: '#4caf50' }} />
            )}
            <span>{row.status}</span>
        </div>
    );
};

const buildFolioColumn = (onDematClick: () => void): TableColumn => ({
    field: 'folio',
    headerName: 'FOLIO',
    renderCell: ({ row }: { row: FolioLockInfo }) => (
        <div className="demat-folio-cell">
            <span>{row.folio}</span>
            {row.isDemat && (
                <button
                    type="button"
                    className="demat-badge"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDematClick();
                    }}
                    aria-label="Demat folio — click to learn how to freeze or unfreeze"
                >
                    DEMAT
                    <svg
                        className="demat-badge__info-icon"
                        aria-hidden="true"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                        <rect
                            x="7.25"
                            y="6.5"
                            width="1.5"
                            height="5"
                            rx="0.75"
                            fill="currentColor"
                        />
                        <circle cx="8" cy="4.5" r="0.85" fill="currentColor" />
                    </svg>
                </button>
            )}
        </div>
    ),
});

const Columns: TableColumn[] = [
    // folio column is inserted dynamically in the component (see buildFolioColumn)
    // so that the DEMAT badge can open the info dialog via setState.
    {
        field: 'amcName',
        headerName: 'Fund House',
        width: 350,
        filterBy: true,
    },
    {
        field: 'investorName',
        headerName: 'PRIMARY HOLDER',
        width: 280,
    },
    {
        field: 'availableUnits',
        headerName: 'Available Units',
        width: 220,
        valueGetter: ({ row }) => decimalFix(row?.availableUnits),
    },
    {
        field: 'availableAmount',
        headerName: 'Available Amount',
        width: 220,
        valueGetter: ({ row }) => row?.availableAmount,
    },
    {
        field: 'status',
        renderCell: _renderStatus,
        headerName: 'Status',
        width: 150,
    },
    {
        isFilter: true,
        field: 'assetType',
        headerName: 'Category',
        filterBy: true,
        renderStaticFilters: [EQUITY, LIQUID, DEBT, OTHER],
    },
];

function LockUnlockFolios(props: any) {
    const [rowData, setRowData] = useState<FolioLockInfo[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [operation, setOperation] = useState<'L' | 'U'>('L'); // Default to Lock
    const [showOptionsDialog, setShowOptionsDialog] = useState<boolean>(false);
    const [showTerms, setShowTerms] = useState<boolean>(false);
    const [showDematInfo, setShowDematInfo] = useState<boolean>(false);
    const dematInfoHeadingRef = useRef<HTMLHeadingElement>(null);
    useDialogAriaHidden(showDematInfo);
    const [trxnType, setTrxnType] = useState<'F' | 'B'>('F');
    const [groupValue, setGroupValue] = useState<string>('');
    const groupBy: 'mobile' | 'email' =
        props.userRegisteredWith === RegisterWith.Email ? 'mobile' : 'email';
    const allColumns: TableColumn[] = useMemo(
        () => [buildFolioColumn(() => setShowDematInfo(true)), ...Columns],
        [],
    );

    const { selectedRows, setSelectedRows, tableColumns, tableRows, ...filters } =
        useTableWithCollapse<FolioLockInfo[]>({ rows: rowData, columns: allColumns, loading });

    useEffect(() => {
        setLoading(true);
        async function fetchPortfolio() {
            const payload: LIST_PROTFOLIO_PAYLOAD = {
                pan: props.pan,
                mobile: props.mobile,
                pekrn: props.pekrn,
                reqId: props.reqId,
            };
            try {
                await props.getPortfolio(payload);
                setLoading(false);
            } catch (err: any) {
                alert(err.message);
                setLoading(false);
            }
        }
        fetchPortfolio();
    }, [props.getPortfolio, props.pan, props.mobile, props.pekrn, props.reqId]);

    useEffect(() => {
        const { portfolios = {} } = props;
        const { data: folioList = [] } = portfolios;

        const folioMap = new Map<string, any>();
        folioList.forEach((portfolioData: PORTFOLIO_DATA_RESPONSE) => {
            (portfolioData.schemes || []).forEach((scheme: SCHEMES_RESPONSE) => {
                const folioKey = scheme.folio;
                if (!folioKey) return;
                const units = Number(scheme.availableUnits) || 0;
                const amount = Number(scheme.availableAmount) || 0;
                const existing = folioMap.get(folioKey);
                if (existing) {
                    existing.availableUnits = (Number(existing.availableUnits) || 0) + units;
                    existing.availableAmount = (Number(existing.availableAmount) || 0) + amount;
                    return;
                }
                folioMap.set(folioKey, {
                    ...scheme,
                    availableUnits: units,
                    availableAmount: amount,
                });
            });
        });

        const consolidatedFolios = Array.from(folioMap.values());

        const evaluatedFolios = getMatchAndUnMatchedFunds(
            lockUnlockFolioFilters,
            consolidatedFolios,
            true,
        );

        const folioRows: FolioLockInfo[] = (evaluatedFolios || [])
            .map((fund: any, index: number) => {
                if (!fund) {
                    return null;
                }
                const isLocked = fund.isLocked === 'Y' || fund.isLocked === true;
                const isDematFolio = fund.isDemat === 'Y';
                const isDisabled = !!fund.isDisabled && !isDematFolio;
                const disabledReason =
                    fund.isDisabled && !isDematFolio
                        ? getErrorMessage(fund, lockUnlockFolioFilters)
                        : '';
                return {
                    id: index.toString(),
                    amc: fund.amc || '',
                    amcName: fund.amcName || '',
                    folio: fund.folio || '',
                    itemNo: index.toString(),
                    status: isLocked ? 'Locked' : 'Unlocked',
                    isLocked,
                    isDisabled,
                    disabledReason,
                    isDemat: isDematFolio,
                    isExcludeFromSelectAll: isDematFolio,
                    holderType: fund.modeOfHolding || '',
                    jointHolders: fund.jointHolders || [],
                    nominationStatus: fund.nomineeStatus === 'Y' ? 'Has Nominee' : 'No Nominee',
                    investorName: fund.investorName || '',
                    mobile: fund.mobile || '',
                    email: fund.email || '',
                    assetType: fund.assetType || fund.schemeType || '',
                    availableUnits: Number(fund.availableUnits) || 0,
                };
            })
            .filter(Boolean) as FolioLockInfo[];

        setRowData(folioRows);
    }, [props.portfolios]);

    const normalizeGroupValue = (value: string = '') => {
        const trimmed = (value || '').trim().toLowerCase();
        if (!trimmed) return '';
        return groupBy === 'mobile' ? stripDialCode(trimmed) : trimmed;
    };

    const groupOptions = useMemo(() => {
        const set = new Set<string>();
        rowData.forEach((row) => {
            const value = normalizeGroupValue(row[groupBy]);
            if (value) set.add(value);
        });
        return Array.from(set);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData, groupBy]);

    useEffect(() => {
        if (groupOptions.length && !groupOptions.includes(groupValue)) {
            setGroupValue(groupOptions[0]);
        }
        if (!groupOptions.length && groupValue) {
            setGroupValue('');
        }
    }, [groupOptions, groupValue]);

    useEffect(() => {
        setSelectedRows([]);
    }, [groupValue, operation]);

    const filteredRowData = useMemo(() => {
        return rowData.filter((row) => {
            const groupMatch = !groupValue || normalizeGroupValue(row[groupBy]) === groupValue;
            if (!groupMatch) return false;
            if (operation === 'L') {
                // Show unlocked folios for locking operation
                return !row.isLocked;
            }
            // Show locked folios for unlocking operation
            return row.isLocked;
        });
    }, [rowData, operation, groupBy, groupValue]);

    const groupLabel = groupBy === 'mobile' ? 'Mobile Number' : 'Email';
    const maskGroupValue = (value: string) =>
        groupBy === 'mobile' ? maskMobileNumber(value) : maskEmail(value);

    // For isRowSelectable prop on TableWithCollapse
    const handleRowSelectable = ({ row }: { row: any }) => {
        if (row.isDemat) {
            setShowDematInfo(true);
            return false;
        }
        return true;
    };

    // For onClick on the wrapper div (accessibility fallback)
    const handleTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;

        if (target.closest('.demat-badge')) return;

        const checkboxCell = target.closest('td[headers="header-row-selection"]');
        if (checkboxCell) {
            const tableRow = checkboxCell.closest('tr');
            if (tableRow?.querySelector('.demat-badge')) {
                setShowDematInfo(true);
            }
            return;
        }

        const mobileRow = target.closest('.mobile-table-outer');
        if (mobileRow?.querySelector('.demat-badge')) {
            setShowDematInfo(true);
        }
    };

    const TableBody = useMemo(() => {
        return (
            <TableWithCollapse
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                columns={tableColumns}
                rows={filteredRowData}
                toolTipTextGetter={({ row }) => row?.disabledReason || ''}
                toolTipEnabled
                checkBoxEnabled
                isRowSelectable={handleRowSelectable}
                {...filters}
            />
        );
    }, [tableColumns, filteredRowData, selectedRows, filters]);

    const handleProceed = () => {
        if (selectedRows.length === 0) {
            alert('Please select at least one folio');
            return;
        }

        if (!props.pan) {
            alert('PAN information is missing. Please log in again.');
            return;
        }

        setTrxnType('F');
        setShowOptionsDialog(true);
    };

    const closeOptionsDialog = () => {
        setShowOptionsDialog(false);
    };

    const handleConfirmOptions = () => {
        setShowOptionsDialog(false);
        props.history.push('/portal/nct/lock-unlock-folios-otp', {
            selectedFolios: selectedRows,
            operation: operation, // Use current operation state
            trxnType,
        });
    };

    return (
        <div className="curved-bg">
            <ActionHeader header={dashboard?.LockUnlockFolios?.LockUnlockFoliosHeading} />
            <div className="curved-bg-content curved-bg-table-outer">
                <div className="curved-bg-content-table">
                    <h5>{dashboard?.LockUnlockFolios?.LockUnlockFoliosInfo}</h5>

                    <div className="lock-unlock-controls">
                        <div className="lock-unlock-controls__radios">
                            <label>
                                <input
                                    type="radio"
                                    name="operation"
                                    value="L"
                                    checked={operation === 'L'}
                                    onChange={() => setOperation('L')}
                                />
                                <span>Lock Folios</span>
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="operation"
                                    value="U"
                                    checked={operation === 'U'}
                                    onChange={() => setOperation('U')}
                                />
                                <span>Unlock Folios</span>
                            </label>
                        </div>

                        {groupOptions.length > 0 && (
                            <FormControl
                                variant="outlined"
                                size="small"
                                className="lock-unlock-controls__group-select"
                            >
                                <InputLabel id="lock-unlock-group-label">{groupLabel}</InputLabel>
                                <Select
                                    labelId="lock-unlock-group-label"
                                    label={groupLabel}
                                    value={groupValue}
                                    onChange={(e) => setGroupValue(e.target.value as string)}
                                >
                                    {groupOptions.map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            {maskGroupValue(opt)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </div>

                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <div className="curved-table-content" onClick={handleTableClick}>
                        {loading || filteredRowData.length ? (
                            TableBody
                        ) : (
                            <NoFoliosFound info={dashboard.LockUnlockFolios.EmptyFoliosInfo} />
                        )}
                    </div>
                </div>
                <div className="lock-unlock-disclaimers">
                    <FaqLink section={LOCK_UNLOCK_FOLIOS} />
                    <span className="terms-link">
                        <a onClick={() => setShowTerms(true)}>
                            {dbCommercial.exploreMfs.mfInvestment.termsLink}
                        </a>
                    </span>
                </div>
                <div className="curved-bg-content-inner-bottom">
                    <div className="table-submit">
                        <SubmitButton
                            onClick={handleProceed}
                            className="customBtn"
                            disabled={!selectedRows.length}
                            label={`Proceed to ${operation === 'L' ? 'Lock' : 'Unlock'} ${
                                selectedRows.length
                            } Folio(s)`}
                        />
                    </div>
                </div>
            </div>
            <Dialog
                open={showOptionsDialog}
                onClose={closeOptionsDialog}
                maxWidth="md"
                fullWidth
                aria-labelledby="lock-unlock-options"
                classes={{ paper: 'lock-unlock-options-modal__paper' }}
            >
                <div className="lock-unlock-options-modal">
                    <div className="lock-unlock-options-modal__header">
                        <div className="lock-unlock-options-modal__icon-tile">
                            {operation === 'L' ? <Lock /> : <LockOpen />}
                        </div>
                        <div className="lock-unlock-options-modal__heading">
                            <h3>{operation === 'L' ? 'Lock Folios' : 'Unlock Folios'}</h3>
                            <p>
                                {operation === 'L'
                                    ? 'Choose what transactions to restrict on the selected folios'
                                    : 'Choose what transactions to re-enable on the selected folios'}
                            </p>
                            <span className="lock-unlock-options-modal__pill">
                                <span className="lock-unlock-options-modal__pill-icon">≡</span>
                                {selectedRows.length}{' '}
                                {selectedRows.length === 1 ? 'folio' : 'folios'} selected
                            </span>
                        </div>
                    </div>
                    <div className="lock-unlock-options-modal__body">
                        <span className="lock-unlock-options-modal__scope-label">
                            Restriction Scope
                        </span>
                        <div className="lock-unlock-options-modal__cards">
                            {[
                                {
                                    value: 'F' as const,
                                    title: 'Debit Transactions',
                                    subtitle: 'Restrict financial outflows only',
                                },
                                {
                                    value: 'B' as const,
                                    title: 'Debit + Service Requests',
                                    subtitle: 'Financial outflows & account-level changes',
                                },
                            ].map((opt) => {
                                const selected = trxnType === opt.value;
                                return (
                                    <button
                                        type="button"
                                        key={opt.value}
                                        className={`lock-unlock-options-modal__card${
                                            selected
                                                ? ' lock-unlock-options-modal__card--selected'
                                                : ''
                                        }`}
                                        onClick={() => setTrxnType(opt.value)}
                                        aria-pressed={selected}
                                    >
                                        <span
                                            className={`lock-unlock-options-modal__radio${
                                                selected
                                                    ? ' lock-unlock-options-modal__radio--selected'
                                                    : ''
                                            }`}
                                        />
                                        <span className="lock-unlock-options-modal__card-text">
                                            <span className="lock-unlock-options-modal__card-title">
                                                {opt.title}
                                            </span>
                                            <span className="lock-unlock-options-modal__card-subtitle">
                                                {opt.subtitle}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="lock-unlock-options-modal__footer">
                        <a className="lock-unlock-options-modal__details-toggle"></a>
                        <div className="lock-unlock-options-modal__actions">
                            <Button
                                className="lock-unlock-options-modal__btn lock-unlock-options-modal__btn--cancel"
                                onClick={closeOptionsDialog}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="lock-unlock-options-modal__btn lock-unlock-options-modal__btn--primary"
                                onClick={handleConfirmOptions}
                            >
                                Proceed to {operation === 'L' ? 'Lock' : 'Unlock'}
                            </Button>
                        </div>
                    </div>
                </div>
            </Dialog>
            <Dialog fullWidth maxWidth={'lg'} open={showTerms} aria-labelledby="terms-modal">
                <div className="terms-modal">
                    <div className="heading">
                        <h2>{common.termsConditions}</h2>
                        <a className="close">
                            <img
                                onClick={() => setShowTerms(false)}
                                src="/images/close-white.svg"
                                alt="icon"
                            />
                        </a>
                    </div>
                    <div className="content-outer">
                        <div className="content-inner">
                            <p>
                                I/We hereby understand that this facility is voluntary, and I/We
                                am/are well aware of related consequences, and I will be solely
                                responsible for any actions regarding transactions viz., transaction
                                rejection, inability to place transactions etc., or communication.
                                I/We acknowledge that the lock will be applied only for the chosen
                                actions. I/We acknowledge that AMC/RTA is not liable for any
                                financial impact due to inability to transact during the lock
                                period. I understand that despite locking of folio, my transaction
                                details will continue to be shared with my Distributor/RIA to enable
                                them in complying with above actions.
                            </p>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/* ── DEMAT info popup ── */}
            <Dialog
                open={showDematInfo}
                onClose={() => setShowDematInfo(false)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="demat-info-heading"
                aria-modal="true"
                role="dialog"
                classes={{ paper: 'demat-info-modal__paper' }}
                PaperProps={{
                    style: {
                        borderRadius: 16,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: '90vh',
                    },
                }}
                TransitionProps={{
                    onEntered: () => dematInfoHeadingRef.current?.focus(),
                }}
            >
                <div className="demat-info-modal">
                    {/* ── Purple header ── */}
                    <div className="demat-info-modal__header">
                        <div className="demat-info-modal__header-text">
                            <div className="demat-info-modal__title-row">
                                <span className="demat-info-modal__lock-icon" aria-hidden="true">
                                    🔒
                                </span>
                                <h3 id="demat-info-heading" ref={dematInfoHeadingRef} tabIndex={-1}>
                                    This folio is held in Demat mode
                                </h3>
                            </div>
                            <p className="demat-info-modal__subtitle">
                                Demat holdings can&apos;t be locked/unlocked here. Use your
                                Depository&apos;s facility below.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="demat-info-modal__close"
                            onClick={() => setShowDematInfo(false)}
                            aria-label="Close Demat mode dialog"
                        >
                            ✕
                        </button>
                    </div>
                    <div className="demat-info-modal__body">
                        <section
                            aria-labelledby="demat-info-nsdl-label"
                            className="demat-info-modal__section"
                        >
                            <div className="demat-info-modal__section-heading">
                                <span
                                    id="demat-info-nsdl-label"
                                    className="demat-info-modal__section-name"
                                >
                                    NSDL
                                </span>
                                <span className="demat-info-modal__depository-pill">
                                    Depository
                                </span>
                            </div>
                            <ol className="demat-info-modal__list">
                                <li>
                                    <strong>Via DP:</strong> Contact your Depository Participant
                                    (broker) and submit a request to freeze your demat account.
                                </li>
                                <li>
                                    <strong>Via Speed-e:</strong> Use the NSDL SPEED-e facility to
                                    manage the freeze/unfreeze directly if registered for the
                                    service.
                                </li>
                            </ol>
                            <div className="demat-info-modal__actions">
                                <a
                                    href="#"
                                    role="link"
                                    className="demat-info-modal__cta demat-info-modal__cta--filled"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        isMobileDevice()
                                            ? window.open('https://eservices.nsdl.com/', '_system')
                                            : window.open('https://eservices.nsdl.com/', '_blank');
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            isMobileDevice()
                                                ? window.open(
                                                      'https://eservices.nsdl.com/',
                                                      '_system',
                                                  )
                                                : window.open(
                                                      'https://eservices.nsdl.com/',
                                                      '_blank',
                                                  );
                                        }
                                    }}
                                >
                                    Go to NSDL Speed-e →
                                </a>
                            </div>
                        </section>

                        <div className="demat-info-modal__divider" role="separator" />

                        <section
                            aria-labelledby="demat-info-cdsl-label"
                            className="demat-info-modal__section"
                        >
                            <div className="demat-info-modal__section-heading">
                                <span
                                    id="demat-info-cdsl-label"
                                    className="demat-info-modal__section-name"
                                >
                                    CDSL
                                </span>
                                <span className="demat-info-modal__depository-pill">
                                    Depository
                                </span>
                            </div>
                            <p className="demat-info-modal__desc">
                                CDSL&apos;s debit freeze facility secures demat accounts by
                                restricting outgoing security transfers (sales) while allowing
                                credits. Investors can initiate this via CDSL Easi or by submitting
                                a form to their Depository Participant (DP).
                            </p>
                            <div className="demat-info-modal__actions">
                                <a
                                    href="#"
                                    role="link"
                                    className="demat-info-modal__cta demat-info-modal__cta--filled"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        isMobileDevice()
                                            ? window.open(
                                                  'https://web.cdslindia.com/myeasitoken/home/login',
                                                  '_system',
                                              )
                                            : window.open(
                                                  'https://web.cdslindia.com/myeasitoken/home/login',
                                                  '_blank',
                                              );
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            isMobileDevice()
                                                ? window.open(
                                                      'https://web.cdslindia.com/myeasitoken/home/login',
                                                      '_system',
                                                  )
                                                : window.open(
                                                      'https://web.cdslindia.com/myeasitoken/home/login',
                                                      '_blank',
                                                  );
                                        }
                                    }}
                                >
                                    EasiEasiest →
                                </a>
                            </div>
                        </section>
                    </div>

                    {/* ── Footer disclaimer ── */}
                    <div className="demat-info-modal__footer">
                        <span className="demat-info-modal__footer-icon" aria-hidden="true">
                            ⓘ
                        </span>
                        <p>
                            You will be redirected to your Depository&apos;s official portal outside
                            MFCentral.
                        </p>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

export default connect(
    ({ authReducer, portfolio }: RootStateType) => ({ ...authReducer, ...portfolio }),
    { getPortfolio },
)(LockUnlockFolios);
